// File: Pages/Reports/utils/ReportConfigProvider.jsx

import React, { createContext, useState, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useAPI } from '../../../Contexts/APIContext'
import defaultFormConfigs from './formConfigs'
import { FAQ_DATA as defaultFaqData } from './faqData'
import { REPORT_TYPE_OPTIONS as defaultReportTypes } from './reportTypes'

const ReportConfigContext = createContext()

/**
 * Provider component that fetches and manages remote configurations
 * Falls back to local defaults if remote fetch fails
 */
export const ReportConfigProvider = ({ children }) => {
  const { getReportConfig } = useAPI() //TODO: for updating forms remotely - currently no API all to get config
  
  const [config, setConfig] = useState({
    isLoading: true,
    formConfigs: defaultFormConfigs,
    faqData: defaultFaqData,
    reportTypes: defaultReportTypes,
    faqConfig: {
      title: 'Self-service Options',
      subtitle: 'Here are some resources that might help you resolve your issue:',
      yesButtonText: 'Yes, this helped me resolve my issue',
      noButtonText: 'No, I still need help'
    },
    error: null
  })
  
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Check if the API has a getReportConfig method
        if (typeof getReportConfig !== 'function') {
          console.log('Remote config not available, using local defaults')
          setConfig(prev => ({ ...prev, isLoading: false }))
          return
        }
        
        // Fetch the configuration
        const response = await getReportConfig()
        
        if (response) {
          // Validate incoming data before using it
          const updatedConfig = {
            isLoading: false,
            error: null
          }
          
          // Update form configs if provided
          if (response.formConfigs && typeof response.formConfigs === 'object') {
            updatedConfig.formConfigs = response.formConfigs
          }
          
          // Update FAQ data if provided
          if (response.faqData && typeof response.faqData === 'object') {
            updatedConfig.faqData = response.faqData
          }
          
          // Update report types if provided
          if (Array.isArray(response.reportTypes) && response.reportTypes.length > 0) {
            updatedConfig.reportTypes = response.reportTypes
          }
          
          // Update FAQ UI config if provided
          if (response.faqConfig && typeof response.faqConfig === 'object') {
            updatedConfig.faqConfig = {
              ...config.faqConfig,
              ...response.faqConfig
            }
          }
          
          setConfig(prev => ({ ...prev, ...updatedConfig }))
        } else {
          // If response is empty or invalid, use defaults
          setConfig(prev => ({ ...prev, isLoading: false }))
        }
      } catch (error) {
        console.error('Error fetching report configuration:', error)
        setConfig(prev => ({ 
          ...prev, 
          isLoading: false,
          error: 'Failed to load remote configuration. Using default configuration instead.'
        }))
      }
    }
    
    fetchConfig()
  }, [getReportConfig])

  return (
    <ReportConfigContext.Provider value={config}>
      {children}
    </ReportConfigContext.Provider>
  )
}

ReportConfigProvider.propTypes = {
  children: PropTypes.node.isRequired
}

// Custom hook to use the report configuration
export const useReportConfig = () => {
  const context = useContext(ReportConfigContext)
  if (context === undefined) {
    throw new Error('useReportConfig must be used within a ReportConfigProvider')
  }
  return context
}

export default ReportConfigProvider